import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
/** Load HERE Maps JS from npm (no CDN). Exposes global `H`. */
import '@here/maps-api-for-javascript/bin/mapsjs.bundle';
import { MapPin } from '@loup-transload-ui-app/common-interfaces';
import { HERE_MAP_KEYS } from '@loup-transload-ui-app/page-translation-keys';
import { TranslationService } from 'shared/common-services/src/lib/on-load-services/translation.service';

type ContactPayload = {
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  managerName?: string;
  isLoup?: boolean;
  // internal
  __index?: number;
  __isLoup?: boolean;
};

@Component({
  selector: 'lib-here-map',
  standalone: true,
  templateUrl: './here-map.component.html',
  styleUrls: ['./here-map.component.scss', './here-map-bubble.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class HereMapComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  public translationService = inject(TranslationService);
  /** ===== Inputs ===== */
  @Input() zoom = 10;
  @Input() lat = 33.88102;
  @Input() lng = -87.351448;
  @Input() width = '100%';
  @Input() height = '540px';
  @Input() markerPoints: MapPin[] = [];
  @Input() smallScreen = false;
  /** ===== Outputs ===== */
  @Output() notify = new EventEmitter<H.map.ChangeEvent>();
  @Output() selectionChanged = new EventEmitter<number | null>();
  /** ===== Internals ===== */
  public apiKey = this.translationService.translate(
    HERE_MAP_KEYS.LABEL.API_KEY
  );
  @ViewChild('map', { static: true }) mapDiv!: ElementRef<HTMLDivElement>;
  private map?: H.Map;
  private resizeObserver?: ResizeObserver;
  private markersGroup?: H.map.Group;
  public indexToMarker = new Map<number, H.map.Marker>();
  selectedIndex: number | null = null;
  private prevSelectedIndex: number | null = null;
  private openBubble?: H.map.DomMarker;
  /** Icons for normal and LOUP */
  private normalIcons!: {
    normal: H.map.Icon;
    hover: H.map.Icon;
    selected: H.map.Icon;
  };
  private loupIcons!: {
    normal: H.map.Icon;
    hover: H.map.Icon;
    selected: H.map.Icon;
  };
  /** prevent immediate close of bubble after marker tap */
  private skipNextMapTapClose = false;
  /** Config */
  private readonly MIN_ZOOM = 2;
  private readonly MAX_ZOOM = 15;
  private readonly MIN_SELECT_ZOOM = 7;
  public loupMarker = 'assets/images/logo_swoop.png';
  public normalMarker = 'assets/images/blue_dot.png';
  public isTableOpen = true;
  public isMobile = false; // drives your @if(isMobile) in the template
  private mapEvents?: H.mapevents.MapEvents;
  public behavior?: H.mapevents.Behavior;
  private mq!: MediaQueryList;
  private onMqChange = (e: MediaQueryListEvent) => {
    this.isMobile = e.matches;
  };

  /** ========= Lifecycle ========= */

  ngOnInit(): void {
    /** 1) Init isMobile  */
    this.mq = window.matchMedia('(max-width: 768px)');
    this.isMobile = this.mq.matches;
    /** 2) React to changes (orientation/resize) */
    if (this.mq.addEventListener) {
      this.mq.addEventListener('change', this.onMqChange);
    } else {
      /** Safari < 14 */
      (this.mq as any).addListener?.(this.onMqChange);
    }
    /** 3) Optional: persist expand/collapse choice */
    const saved = localStorage.getItem('hereMap.tableOpen');
    if (saved !== null) this.isTableOpen = saved === 'true';
  }

  toggleTable(): void {
    this.isTableOpen = !this.isTableOpen;
    try {
      localStorage.setItem('hereMap.tableOpen', String(this.isTableOpen));
    } catch {
      console.log('error');
    }
  }

  ngAfterViewInit(): void {
    if (!this.mapDiv || this.map) return;

    // Remove any existing children in the map container (defensive)
    while (this.mapDiv.nativeElement.firstChild) {
      this.mapDiv.nativeElement.removeChild(
        this.mapDiv.nativeElement.firstChild
      );
    }

    const platform = new H.service.Platform({ apikey: this.apiKey });
    const layers = platform.createDefaultLayers();

    this.map = new H.Map(
      this.mapDiv.nativeElement,
      (layers as any).vector.normal.map,
      {
        pixelRatio: Math.max(1, window.devicePixelRatio || 1),
        center: { lat: this.lat, lng: this.lng },
        zoom: this.clampZoom(this.zoom),
      }
    );

    /** interactions (no H.ui) */
    this.mapEvents = new H.mapevents.MapEvents(this.map);
    this.behavior = new H.mapevents.Behavior(this.mapEvents);

    /** ResizeObserver — throttled only */
    let resizeRaf = 0;
    this.resizeObserver = new ResizeObserver(() => {
      if (!this.map) return;
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        this.map?.getViewPort().resize();
      });
    });
    this.resizeObserver.observe(this.mapDiv.nativeElement);

    /** forward map events */
    this.map.addEventListener('mapviewchange', (e: H.map.ChangeEvent) =>
      this.notify.emit(e)
    );

    /** ===== ICONS (UNCHANGED) ===== */
    this.normalIcons = {
      normal: new H.map.Icon(this.normalMarker, {
        size: { w: 10, h: 10 },
        anchor: { x: 5, y: 5 },
      }),
      hover: new H.map.Icon(this.normalMarker, {
        size: { w: 12, h: 12 },
        anchor: { x: 6, y: 6 },
      }),
      selected: new H.map.Icon(this.normalMarker, {
        size: { w: 12, h: 12 },
        anchor: { x: 6, y: 6 },
      }),
    };

    this.loupIcons = {
      normal: new H.map.Icon(this.loupMarker, { size: { w: 12, h: 14 } }),
      hover: new H.map.Icon(this.loupMarker, { size: { w: 13, h: 15 } }),
      selected: new H.map.Icon(this.loupMarker, { size: { w: 13, h: 15 } }),
    };

    /** Render markers after first paint */
    requestAnimationFrame(() => this.renderMarkers());

    /** close bubble on map tap */
    this.map.addEventListener('tap', (evt: H.mapevents.Event) => {
      if (this.skipNextMapTapClose) {
        this.skipNextMapTapClose = false;
        return;
      }
      const targetEl = (evt as any).originalEvent?.target as
        | HTMLElement
        | undefined;
      if (targetEl?.closest('.here-bubble')) return;
      this.closeBubble();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.map) return;
    if (changes['zoom'])
      this.map.setZoom(this.clampZoom(changes['zoom'].currentValue), true);
    if (changes['lat'])
      this.map.setCenter(
        { lat: changes['lat'].currentValue, lng: this.lng },
        true
      );
    if (changes['lng'])
      this.map.setCenter(
        { lat: this.lat, lng: changes['lng'].currentValue },
        true
      );
    if (changes['markerPoints']) {
      /** reset state and re-render */
      const hadSel = this.selectedIndex !== null;
      this.selectedIndex = null;
      this.prevSelectedIndex = null;
      this.indexToMarker.clear();
      this.closeBubble();
      this.renderMarkers();
      if (hadSel) this.selectionChanged.emit(null);
    }
  }

  ngOnDestroy(): void {
    try {
      this.resizeObserver?.disconnect();
    } catch {
      console.log('test');
    }
    try {
      this.map?.dispose();
    } catch {
      console.log('test');
    }

    if (!this.mq) return;
    if (this.mq.removeEventListener) {
      this.mq.removeEventListener('change', this.onMqChange);
    } else {
      (this.mq as any).removeListener?.(this.onMqChange);
    }
  }

  /** ===== Public API for parent table ===== */
  openAtIndex(index: number): void {
    // same as row click
    void this.selectRowIndex(index, true, true);
  }

  clearSelection(): void {
    if (this.selectedIndex !== null) {
      const m = this.indexToMarker.get(this.selectedIndex);
      if (m) {
        const data = m.getData?.() ?? {};
        const set = data.__isLoup ? this.loupIcons : this.normalIcons;
        m.setIcon(set.normal);
        m.setZIndex(0);
      }
    }
    this.selectedIndex = null;
    this.prevSelectedIndex = null;
    this.closeBubble();
    this.selectionChanged.emit(null);
  }

  /** ===== Template-bound ===== */
  onRowClick(index: number): void {
    void this.selectRowIndex(index, true, true);
  }

  onRowEnter(index: number): void {
    const m = this.indexToMarker.get(index);
    if (!m || this.selectedIndex === index) return;
    const data = m.getData?.() ?? {};
    const set = data.__isLoup ? this.loupIcons : this.normalIcons;
    m.setIcon(set.hover);
    m.setZIndex(500);
  }

  onRowLeave(index: number): void {
    const m = this.indexToMarker.get(index);
    if (!m) return;
    const isSelected = this.selectedIndex === index;
    const data = m.getData?.() ?? {};
    const set = data.__isLoup ? this.loupIcons : this.normalIcons;
    m.setIcon(isSelected ? set.selected : set.normal);
    m.setZIndex(isSelected ? 1000 : 0);
  }

  zoomIn(): void {
    if (!this.map) return;
    this.map.setZoom(this.clampZoom(this.map.getZoom() + 1), true);
  }

  zoomOut(): void {
    if (!this.map) return;
    this.map.setZoom(this.clampZoom(this.map.getZoom() - 1), true);
  }
  /** ===== Core rendering ===== */
  private renderMarkers(): void {
    if (!this.map) return;
    if (this.markersGroup) {
      this.map.removeObject(this.markersGroup);
      this.markersGroup = undefined;
    }
    const group = new H.map.Group();
    const markers: H.map.Marker[] = [];
    const points = this.markerPoints ?? [];
    for (const [i, p] of points.entries()) {
      const isLoup = !!p.data?.isLoup;
      const set = isLoup ? this.loupIcons : this.normalIcons;

      const marker = new H.map.Marker(
        { lat: p.lat, lng: p.lng },
        {
          icon: set.normal,
          data: {
            ...p.data,
            __index: i,
            __isLoup: isLoup,
          } as ContactPayload,
        }
      );

      /** hover */
      marker.addEventListener('pointerenter', () => {
        if (this.selectedIndex === i) return;
        marker.setIcon(set.hover);
        marker.setZIndex(500);
      });

      marker.addEventListener('pointerleave', () => {
        const isSel = this.selectedIndex === i;
        marker.setIcon(isSel ? set.selected : set.normal);
        marker.setZIndex(isSel ? 1000 : 0);
      });

      /** tap */
      marker.addEventListener('tap', async (evt: any) => {
        this.skipNextMapTapClose = true;
        await this.selectRowIndex(i, true, true);
        evt?.stopPropagation?.();
      });

      markers.push(marker);
      this.indexToMarker.set(i, marker);
    }
    if (markers.length) {
      group.addObjects(markers);
      this.map.addObject(group);
      this.markersGroup = group;
      // fit bounds to markers
      const bbox = group.getBoundingBox();
      if (bbox) {
        const vp = this.map.getViewPort();
        vp.setPadding(24, 24, 24, 24);
        this.map.getViewModel().setLookAtData({ bounds: bbox }, true);
        if (this.smallScreen === true) {
          // For small screens, clamp zoom immediately after fit
          setTimeout(() => {
            vp.setPadding(0, 0, 0, 0);
            if (this.map && this.map.getZoom() > 3) {
              this.map.setZoom(3, true);
            }
          }, 350);
        } else {
          const onEnd = () => {
            this.map?.removeEventListener('mapviewchangeend', onEnd);
            vp.setPadding(0, 0, 0, 0);
            if (this.map) {
              const z = this.map.getZoom();
              if (z > 10) this.map.setZoom(10, true);
            }
          };
          this.map.addEventListener('mapviewchangeend', onEnd);
        }
      } else {
        // fallback: always set zoom and center
        this.map.setCenter({ lat: 33.88102, lng: -87.351448 }, true);
        if (this.smallScreen === true) {
          this.map.setZoom(3, true);
        } else {
          this.map.setZoom(10, true);
        }
      }
    } else {
      // No markers: center and zoom to USA at zoom 10 (prevents duplicate/tiling)
      this.map.setCenter({ lat: 33.88102, lng: -87.351448 }, true);
      if (this.smallScreen === true) {
        this.map.setZoom(3, true);
      } else {
        this.map.setZoom(10, true);
      }
    }
  }

  /** ===== Selection & centering ===== */
  private async selectRowIndex(
    index: number,
    center = true,
    openBubble = true
  ): Promise<void> {
    if (!this.map) return;
    const next = this.indexToMarker.get(index);
    const prev =
      this.prevSelectedIndex === null
        ? undefined
        : this.indexToMarker.get(this.prevSelectedIndex);
    const prevSelected = this.selectedIndex;
    this.prevSelectedIndex = this.selectedIndex;
    this.selectedIndex = index;
    /** restore prev */
    if (prev && this.prevSelectedIndex !== index) {
      const pdata = prev.getData?.() ?? {};
      const pset = pdata.__isLoup ? this.loupIcons : this.normalIcons;
      prev.setIcon(pset.normal);
      prev.setZIndex(0);
    }
    /**  apply next */
    if (next) {
      const ndata = next.getData?.() ?? {};
      const nset = ndata.__isLoup ? this.loupIcons : this.normalIcons;
      next.setIcon(nset.selected);
      next.setZIndex(1000);
    }
    if (prevSelected !== index) this.selectionChanged.emit(index);
    if (!next) return;
    const coord = next.getGeometry() as H.geo.IPoint;
    const payload = (next.getData?.() as ContactPayload) ?? {};
    if (center) {
      await this.centerOn(coord, this.MIN_SELECT_ZOOM);
    }
    if (openBubble) {
      this.showContactBubble(coord, payload);
    }
  }

  /**
   * Always brings the point into view.
   * If current zoom is less than minZoom, it zooms in to minZoom.
   * Otherwise it just centers smoothly.
   */
  private centerOn(pos: H.geo.IPoint, minZoom: number): Promise<void> {
    return new Promise((resolve) => {
      if (!this.map) {
        resolve();
        return;
      }
      const vm = this.map.getViewModel();
      const targetZoom = Math.max(this.map.getZoom() || minZoom, minZoom);

      const finish = () => {
        this.map?.removeEventListener('mapviewchangeend', finish);
        resolve();
      };
      this.map.addEventListener('mapviewchangeend', finish);
      vm.setLookAtData({ position: pos, zoom: targetZoom }, true);
    });
  }

  /** ===== Overlay ===== */
  private showContactBubble(geo: H.geo.IPoint, info: ContactPayload): void {
    if (!this.map) return;
    this.closeBubble();
    const el = this.buildContactBubbleElement(info);
    const domIcon = new H.map.DomIcon(el);
    const bubble = new H.map.DomMarker(geo, { icon: domIcon, data: info });
    (bubble as any).setVolatility?.(true);
    bubble.setZIndex(10000);
    this.map.addObject(bubble);
    this.openBubble = bubble;
  }

  private closeBubble(): void {
    if (this.openBubble && this.map) {
      this.map.removeObject(this.openBubble);
      this.openBubble = undefined;
    }
  }

  private buildContactBubbleElement(info: ContactPayload): HTMLElement {
    const el = document.createElement('div');
    el.className = 'here-bubble';
    el.innerHTML = `
<div class="here-bubble__content">
  <div class="here-bubble__header">
    <div class="here-bubble__title">
      ${this.escapeHtml(
        info.name ?? (info.isLoup ? 'LOUP Location' : 'Details')
      )}
    </div>
    ${info.isLoup ? '<span class="here-bubble__badge">LOUP</span>' : ''}
  </div>

  <div class="here-bubble__body">
    ${
      info.address
        ? `<div class="here-bubble__line">${this.escapeHtml(
            info.address
          )}</div>`
        : ''
    }
    <div class="here-bubble__line">
      <up-icon name="email" slot="icon" class="align-phone-email"></up-icon>
      <span>
        <a class="email-action"
          href="mailto:${this.escapeHtml(info.email ?? 'info@shiploup.com')}"
          onmouseover="this.style.textDecoration='underline'"
          onmouseout="this.style.textDecoration='none'"
        >
          ${this.escapeHtml(info.email ?? 'info@shiploup.com')}
        </a>
      </span>
    </div>
    <div class="here-bubble__line">
      <up-icon name="phone" slot="icon" class="align-phone-email"></up-icon>
      ${this.escapeHtml(info.phone ?? '1 (800) 303-4544')}
    </div>
  </div>

  <div class="here-bubble__arrow"></div>
</div>
`.trim();
    /** Keep clicks inside bubble from closing it */
    const stop = (e: Event) => e.stopPropagation();
    el.addEventListener('click', stop);
    el.addEventListener('pointerdown', stop);
    el.addEventListener('pointerup', stop);
    /** Close button */
    el.querySelector('.here-bubble__close')?.addEventListener('click', () =>
      this.closeBubble()
    );
    return el;
  }

  /** ===== Utils ===== */
  private clampZoom(z: number): number {
    return Math.max(this.MIN_ZOOM, Math.min(this.MAX_ZOOM, z));
  }

  private buildPinIcon(color: string, size = 24): H.map.Icon {
    const w = size;
    const h = Math.round(size * 1.33);
    const anchor = { x: Math.round(w / 2), y: h - 2 };
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 28 37">
        <path d="M14 36c0 0 12-13.9 12-22C26 6.373 20.627 1 14 1S2 6.373 2 14c0 8.1 12 22 12 22z"
          fill="${color}" stroke="rgba(9,16,28,0.28)" stroke-width="1"/>
        <circle cx="14" cy="14" r="4.5" fill="#fff" opacity="0.9"/>
        <circle cx="14" cy="14" r="2.8" fill="rgba(9,16,28,0.9)"/>
      </svg>`.trim();
    return new H.map.Icon(
      'data:image/svg+xml;utf8,' + encodeURIComponent(svg),
      { size: { w, h }, anchor }
    );
  }

  // Fast, single-pass, no-regex, no replaceAll — Sonar S7781-safe
  private escapeHtml(val: string): string {
    const s = String(val);
    let out = '';
    // Map of characters to HTML entities
    const map: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    };
    // Single pass over the string; avoids double-encoding
    for (const ch of s) {
      out += map[ch] ?? ch;
    }
    return out;
  }

  onIconError(ev: Event) {
    const img = ev.target as HTMLImageElement;
    /** Abort if we already tried a fallback */
    if (img.dataset?.['fallbackApplied'] === 'true') {
      return;
    }
    /**  Use a safe local placeholder */
    img.src = 'assets/img/icon_fallback.png';
    if (img.dataset) {
      img.dataset['fallbackApplied'] = 'true';
    }
  }
}
